// format.js — number / currency / date formatting for the cost console.
// All money renders with tabular figures (the body sets font-variant-numeric),
// and INR is the default display currency (contract §4).

const DAY_MS = 86400000;

export function isoDay(d) {
  return new Date(d).toISOString().slice(0, 10);
}

export function shiftDay(iso, days) {
  return isoDay(new Date(Date.parse(iso + 'T00:00:00Z') + days * DAY_MS));
}

export function todayIso() {
  return isoDay(new Date());
}

// First day of the month for an ISO day or Date.
export function monthStart(iso) {
  const d = new Date(iso);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

// Inclusive list of ISO days from→to.
export function daysBetween(from, to) {
  const out = [];
  let cur = from;
  let guard = 0;
  while (cur <= to && guard < 4000) { out.push(cur); cur = shiftDay(cur, 1); guard += 1; }
  return out;
}

export function fmtDayShort(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function fmtMonthLabel(ym) {
  const t = Date.parse(ym + '-01T00:00:00Z');
  if (Number.isNaN(t)) return ym;
  return new Date(t).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const CURRENCY_SYMBOL = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };

// Money with a currency symbol. Small values keep more precision so sub-rupee /
// sub-cent per-message rates don't collapse to 0.
export function fmtMoney(value, currency = 'INR') {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const sym = CURRENCY_SYMBOL[currency] || (currency ? currency + ' ' : '');
  const abs = Math.abs(n);
  let digits = 0;
  if (abs > 0 && abs < 1) digits = 4;
  else if (abs < 100) digits = 2;
  return sym + n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtINR(value) {
  return fmtMoney(value, 'INR');
}

// Compact integer/quantity, e.g. 1,203,400 → "1.2M".
export function fmtCompact(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toLocaleString('en-IN', { maximumFractionDigits: 1 }) + 'k';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export function fmtNum(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtPct(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits) + '%';
}

// Percentage change now vs prev, with sign and null-safety.
export function pctChange(now, prev) {
  const a = Number(now); const b = Number(prev);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
  return ((a - b) / Math.abs(b)) * 100;
}

// Metric slug → human unit label for tables.
export const METRIC_LABEL = {
  'input-tokens': 'input tokens',
  'output-tokens': 'output tokens',
  'audio-seconds': 'audio sec',
  'characters': 'characters',
  'sessions': 'sessions',
  'messages': 'messages',
  'runs': 'runs',
  'billed-cost': 'billed cost',
};

// Provider → cost category (Overview donut buckets), contract §1 provider list.
export const PROVIDER_CATEGORY = {
  openai: 'LLM',
  'azure-openai': 'LLM',
  cerebras: 'LLM',
  'medplum-ai': 'LLM',
  deepgram: 'STT',
  sarvam: 'STT/TTS',
  elevenlabs: 'Voice/TTS',
  gemini: 'Voice/LLM',
  'openai-realtime': 'Voice',
  gupshup: 'Messaging',
  'whatsapp-meta': 'Messaging',
  razorpay: 'Payments',
  aws: 'AWS',
  apple: 'Store',
  'google-play': 'Store',
  other: 'Misc',
};

export function categoryOfProvider(provider) {
  return PROVIDER_CATEGORY[provider] || 'Misc';
}
